import React from 'react';
import { LocaleAllow, MetafieldsData } from '../..//types/IHeader';
import { linkResolver, Subpath } from '../utils/linkResolver';
import { PostMeta } from './utils';

export const getSubpath = (pageType: string) => {
  switch (pageType) {
    case 'DatoCmsClientPage':
      return Subpath.Client;
    case 'DatoCmsBlogArticle':
    case 'DatoCmsCategoryPage':
    case 'DatoCmsSubCategoryPage':
      return Subpath.BlogArticles;
    case 'DatoCmsPillarPage':
      return Subpath.Pillar;
    default:
      return undefined;
  }
};

export interface ISeoMeta {
  metaFields: MetafieldsData;
  __typename: string;
  _allSlugLocales: { locale: string; value: string }[];
  slug: string;
  hero: { title: string; subtitle: string; image?: { url?: string } }[];
  noIndex: boolean;
  noindexNofollow: boolean;
  canonicalUrl?: {
    __typename: string;
    id: string;
    slug: string;
    trackingPageName: {
      title: string;
    };
  };
  treeParent: {
    slug: string;
    _allSlugLocales: {
      locale: string;
      value: string;
    }[];
  };
}

export type ISeoData = {
  data: ISeoMeta;
  locale: LocaleAllow;
  postMeta: PostMeta;
};

export const Seo = ({ data, locale, postMeta }: ISeoData) => {
  const dataSeo = data;
  if (!dataSeo) return null;

  const subpath = getSubpath(dataSeo.__typename);
  const { metaFields } = dataSeo;

  const allSlugLocales = dataSeo?._allSlugLocales
    ?.filter((x) => x.locale !== 'de')
    .map((item) => {
      // In a doc, _allSlugLocales only contains the slugs and
      // locales of its translated versions, but we need their
      // translated parents to build their full url.
      // We find those in the treeParent._allSlugLocales array
      // and add them to their related item
      if (!dataSeo.treeParent) {
        return item;
      }

      const parentSlug = dataSeo.treeParent?._allSlugLocales?.find(
        (x) => x.locale === item.locale,
      )?.value;

      if (!parentSlug) {
        throw new Error(
          `Missing parent translation for page "${dataSeo.slug}" in locale "${item.locale}". (locale slug: "${item.value}")`,
        );
      }

      return {
        ...item,
        treeParent: {
          slug: parentSlug,
        },
      };
    });

  const isBlogArticle = dataSeo.__typename === 'DatoCmsBlogArticle';

  const title = metaFields?.title;

  // For blog articles, use page-specific images (either the meta field or the hero image)
  const image = isBlogArticle
    ? metaFields?.image?.url || data?.hero?.[0]?.image?.url
    : undefined; // For non-blog pages, skip to fallback

  const ogImage = image || postMeta?.fallbackSeoImage?.url;

  const description = metaFields?.description || data?.hero?.[0]?.title;
  const canonicalUrl = dataSeo?.canonicalUrl;

  let canonicalLink = `https://payfit.com${linkResolver(
    dataSeo,
    locale,
    '',
    subpath,
  )}`;

  if (canonicalUrl && canonicalUrl.slug !== dataSeo.slug) {
    canonicalLink = `https://payfit.com${linkResolver(
      canonicalUrl,
      locale,
      '',
      subpath,
    )}`;
  }

  let robotsMeta = 'index, follow';
  if (data?.noIndex) robotsMeta = 'noindex, follow';
  if (data?.noindexNofollow) robotsMeta = 'noindex, nofollow';

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      {ogImage && (
        <meta
          property="og:image"
          content={ogImage + '?auto=format&h=315&w=600'}
        />
      )}
      <meta property="og:type" content="website" />
      <meta
        property="og:url"
        content={`https://payfit.com${linkResolver(
          dataSeo,
          locale,
          '',
          subpath,
        )}`}
      />
      <meta
        name="twitter:card"
        content={metaFields?.twitterCard || `summary_large_image`}
      />
      <meta name="twitter:creator" content="PayFit" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && (
        <meta
          name="twitter:image"
          content={ogImage + '?auto=format&h=315&w=600'}
        />
      )}
      <meta
        name="google-site-verification"
        content="ZHDQE-gryjdkljHv_lMLi3E1jbFVKZ85CG8Qzmkk3ns"
      />

      {<meta content={robotsMeta} name="robots" />}

      <link rel="canonical" href={canonicalLink} />

      {allSlugLocales?.length > 0 ? (
        allSlugLocales?.map((item) => {
          const isAlternate = true;
          const params = '';
          return (
            <link
              rel="alternate"
              href={`https://payfit.com${linkResolver(
                item,
                item?.locale,
                params,
                subpath,
                isAlternate,
              )}`}
              hrefLang={item.locale}
              key={item.locale}
            />
          );
        })
      ) : (
        <link
          rel="alternate"
          hrefLang={locale}
          href={`https://payfit.com${linkResolver(
            dataSeo,
            locale,
            '',
            subpath,
          )}`}
        />
      )}
    </>
  );
};
